// CaretakerFAQ.js
// Public FAQ page for Evermark Memorial caretakers / providers.
// Includes a notice banner at the top about cemetery rules.
// Same accordion pattern as CustomerFAQ — only one item open at a time.
// Route: /caretaker-faq (public, no auth required)
// All language reflects independent contractor status — no employer-employee framing.

import React, { useState } from 'react';

// ===== FAQ DATA =====
// Each section has a title and an array of { question, answer } pairs.
const FAQ_SECTIONS = [
  {
    section: 'Becoming a Caretaker',
    items: [
      {
        question: 'What does a caretaker do?',
        answer: 'Caretakers help maintain and respectfully care for gravesites by cleaning markers, placing decorations, taking photos, and reporting completed work.',
      },
      {
        question: 'Do I need experience?',
        answer: 'No professional experience is required, though attention to detail, reliability, and compassion are very important.',
      },
      {
        // "hiring in" replaced with "accepting caretakers" — IC-appropriate framing
        question: 'What areas are you accepting caretakers?',
        answer: 'We are currently onboarding caretakers in Mahoning and Trumbull County, Ohio and Lawrence County, Pennsylvania.',
      },
      {
        question: 'Is this flexible work?',
        // "roles are flexible and can work around your availability" implied Evermark controls scheduling
        answer: 'Yes. Caretaker opportunities are fully flexible. You choose which jobs to accept and when, with no required hours or minimum commitments.',
      },
    ],
  },
  {
    section: 'Expectations & Work Process',
    items: [
      {
        question: 'What qualities are you looking for?',
        answer: 'We value: Reliability, Respectfulness, Good communication, Professional behavior, Careful attention to memorial sites.',
      },
      {
        question: 'Will I work alone?',
        answer: 'Yes. Visits are completed independently. You manage your own time and approach each job according to the platform standards and guidelines.',
      },
      {
        question: 'Do I need my own transportation?',
        answer: 'Yes. Reliable transportation is required since caretakers travel independently to cemetery locations.',
      },
      {
        question: 'Will I need to take photos?',
        answer: 'Yes. Clear before-and-after photos are an important part of customer updates and service verification.',
      },
    ],
  },
  {
    section: 'Pay & Scheduling',
    items: [
      {
        question: 'How are caretakers paid?',
        answer: 'Caretakers are paid per completed service visit. Payments are processed through the Evermark Memorial platform and sent directly to you after job completion. Rates depend on service type and location.',
      },
      {
        question: 'Can I set my own schedule?',
        // "We aim to work with caretaker availability" implied Evermark directs scheduling
        answer: 'Yes. You decide which jobs to accept and when to complete them. There are no set hours, required shifts, or minimum job commitments — you work entirely on your own schedule.',
      },
      {
        question: 'How often will jobs be available?',
        answer: 'Job availability varies by season, weather, and customer demand. Memorial Day and holidays tend to be the busiest periods. You choose which available jobs to accept.',
      },
    ],
  },
  {
    section: 'Professional Standards',
    items: [
      {
        question: 'What should I wear?',
        answer: 'Caretakers should wear respectful clothing appropriate for outdoor work and cemetery environments.',
      },
      {
        question: 'Are there rules about decorations and cleaning products?',
        answer: 'Yes. Only approved materials and cemetery-safe cleaning methods may be used. Bleach, wire brushes, pressure washers, and abrasive tools are strictly prohibited.',
      },
      {
        question: 'Can I bring family or friends to a job?',
        answer: 'To maintain professionalism and customer trust, only approved caretakers may complete services.',
      },
      {
        question: 'Will I receive any onboarding materials?',
        // "company expectations" → "platform standards and guidelines"
        // "on behalf of Evermark Memorial" → "through the Evermark Memorial platform"
        answer: 'Approved caretakers receive a digital onboarding guide covering platform standards and guidelines, cemetery etiquette, approved cleaning methods, photo requirements, decoration guidelines, and general service procedures. Caretakers are expected to review all materials thoroughly before accepting jobs through the Evermark Memorial platform.',
      },
      {
        question: 'What supplies will I need?',
        answer: 'Basic supplies may include gentle cleaning materials, gloves, water containers, trimming tools, and a smartphone capable of taking clear photos. Caretakers are responsible for sourcing and providing their own supplies.',
      },
      {
        // "Will I be assigned specific cemeteries?" implied Evermark directs job placement
        question: 'Will jobs near my location be available?',
        // "we try to assign caretakers to areas" → jobs are posted, you choose
        answer: 'Jobs are posted for cemeteries in your service area and you choose which ones to accept. The platform surfaces available jobs near your location so you can pick what works for you.',
      },
      {
        question: 'Is this independent contractor work?',
        answer: 'Yes. Caretaker opportunities operate as independent contractor arrangements. You are not an employee of Evermark Memorial. You are responsible for your own taxes, expenses, and business decisions.',
      },
      {
        // "scheduled visit" implied assigned shift; "notify management" implied employment
        question: 'What if I cannot complete a job I have accepted?',
        // "notify management" → "contact the Evermark Memorial support team"
        answer: 'Communication is extremely important. If an issue arises, please contact the Evermark Memorial support team as soon as possible so the customer can be notified and arrangements can be made.',
      },
      {
        // "inspected" → "reviewed for quality"
        question: 'Are completed visits reviewed for quality?',
        answer: 'Yes. Photo documentation and customer feedback help maintain consistent service quality and professionalism across the platform.',
      },
    ],
  },
];

// ===== ACCORDION ITEM =====
// Identical pattern to CustomerFAQ — openKey lifted to parent for single-open behavior.
function AccordionItem({ question, answer, itemKey, openKey, setOpenKey }) {
  const isOpen = openKey === itemKey;

  const toggle = () => {
    setOpenKey(isOpen ? null : itemKey);
  };

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button className="faq-question" onClick={toggle} aria-expanded={isOpen}>
        <span>{question}</span>
        {/* Chevron rotates 180° when open */}
        <span className={`faq-chevron${isOpen ? ' faq-chevron--open' : ''}`}>
          &#8964;
        </span>
      </button>

      <div className={`faq-answer${isOpen ? ' faq-answer--open' : ''}`}>
        <p>{answer}</p>
      </div>
    </div>
  );
}

// ===== MAIN COMPONENT =====
function CaretakerFAQ() {
  // Tracks which accordion item is open — null means all closed
  const [openKey, setOpenKey] = useState(null);

  return (
    <div className="faq-page">

      {/* ===== NAVBAR ===== */}
      <nav className="faq-nav">
        <a href="/" className="faq-nav-logo">Evermark Memorial</a>
        <div className="faq-nav-links">
          <a href="/provider/signup" className="faq-nav-cta">Become a Caretaker</a>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <div className="faq-hero">
        <span className="faq-hero-label">Caretaker Support</span>
        <h1 className="faq-hero-title">Caretaker FAQ</h1>
        <p className="faq-hero-subtitle">
          Everything you need to know about joining and working with the Evermark Memorial caretaker network.
        </p>
        {/* Tab toggle — links back to customer FAQ */}
        <div className="faq-toggle">
          <a href="/faq" className="faq-toggle-btn">For Families</a>
          <span className="faq-toggle-btn faq-toggle-btn--active">For Caretakers</span>
        </div>
      </div>

      {/* ===== NOTICE BANNER ===== */}
      {/* Warm amber box reminding caretakers to follow cemetery rules */}
      <div className="faq-notice">
        <div className="faq-notice-box">
          <span className="faq-notice-icon">⚠️</span>
          <p className="faq-notice-text">
            Please review all cemetery rules and requirements before beginning any memorial cleaning
            or decoration service. Each cemetery may have specific guidelines regarding approved
            cleaning methods, decorations, visiting hours, and conduct on the property. As a
            caretaker providing services through the Evermark Memorial platform, you are expected
            to respect and follow all cemetery regulations while maintaining a professional and
            compassionate presence at every site.
          </p>
        </div>
      </div>

      {/* ===== ACCORDION SECTIONS ===== */}
      <div className="faq-content">
        {FAQ_SECTIONS.map((section, sIdx) => (
          <div key={sIdx} className="faq-section">

            {/* Section heading in brand green */}
            <h2 className="faq-section-title">{section.section}</h2>

            <div className="faq-section-items">
              {section.items.map((item, iIdx) => (
                <AccordionItem
                  key={iIdx}
                  itemKey={`${sIdx}-${iIdx}`}
                  question={item.question}
                  answer={item.answer}
                  openKey={openKey}
                  setOpenKey={setOpenKey}
                />
              ))}
            </div>

          </div>
        ))}
      </div>

      {/* ===== BOTTOM CTA ===== */}
      <div className="faq-cta-block">
        <h3>Ready to get started?</h3>
        <p>Join the Evermark Memorial caretaker network and start earning in your area.</p>
        <a href="/provider/signup">
          <button className="btn-primary">Apply as a Caretaker</button>
        </a>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className="faq-footer">
        <p>© 2026 Evermark Memorial. All rights reserved.</p>
        <div className="faq-footer-links">
          <a href="/terms">Terms of Service</a>
          <a href="/privacy">Privacy Policy</a>
          <a href="/contact">Contact</a>
        </div>
      </footer>

    </div>
  );
}

export default CaretakerFAQ;
